import { cn } from "@dream/ui";
import { type ClipboardEvent, type KeyboardEvent, useEffect, useRef } from "react";

/**
 * Senha de 4 dígitos da visão do cliente.
 *
 * Quatro caixas, teclado numérico no celular, avanço automático e colar o
 * código inteiro de uma vez.
 */

const PIN_SLOTS = [0, 1, 2, 3] as const;

export function PinInput({
  id,
  value,
  onChange,
  disabled = false,
  invalid = false,
  autoFocus = false,
  onComplete,
}: {
  id: string;
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
  invalid?: boolean;
  autoFocus?: boolean;
  onComplete?: (pin: string) => void;
}) {
  const digits = Array.from({ length: 4 }, (_, i) => value[i] ?? "");
  const refs = useRef<Array<HTMLInputElement | null>>([null, null, null, null]);

  useEffect(() => {
    if (autoFocus) refs.current[0]?.focus();
  }, [autoFocus]);

  function setDigit(index: number, raw: string): void {
    const digit = raw.replace(/\D/g, "").slice(-1);
    const next = digits.map((current, i) => (i === index ? digit : current));
    const joined = next.join("").slice(0, 4);
    onChange(joined);
    if (digit !== "" && index < 3) {
      refs.current[index + 1]?.focus();
    }
    if (joined.length === 4) onComplete?.(joined);
  }

  function onKeyDown(index: number, event: KeyboardEvent<HTMLInputElement>): void {
    if (event.key === "Backspace" && digits[index] === "" && index > 0) {
      event.preventDefault();
      const next = digits.map((current, i) => (i === index - 1 ? "" : current)).join("");
      onChange(next);
      refs.current[index - 1]?.focus();
    }
    if (event.key === "ArrowLeft" && index > 0) {
      event.preventDefault();
      refs.current[index - 1]?.focus();
    }
    if (event.key === "ArrowRight" && index < 3) {
      event.preventDefault();
      refs.current[index + 1]?.focus();
    }
  }

  function onPaste(event: ClipboardEvent<HTMLInputElement>): void {
    event.preventDefault();
    const pasted = event.clipboardData.getData("text").replace(/\D/g, "").slice(0, 4);
    if (pasted === "") return;
    onChange(pasted);
    const focusAt = Math.min(pasted.length, 3);
    refs.current[focusAt]?.focus();
    if (pasted.length === 4) onComplete?.(pasted);
  }

  return (
    <fieldset className="m-0 flex justify-center gap-2 border-0 p-0 sm:gap-3">
      <legend className="sr-only">Senha de 4 dígitos</legend>
      {PIN_SLOTS.map((slot) => (
        <input
          key={`${id}-${slot}`}
          ref={(node) => {
            refs.current[slot] = node;
          }}
          id={slot === 0 ? id : undefined}
          inputMode="numeric"
          autoComplete={slot === 0 ? "one-time-code" : "off"}
          pattern="\d*"
          maxLength={1}
          disabled={disabled}
          aria-invalid={invalid || undefined}
          aria-label={`Dígito ${slot + 1} de 4`}
          value={digits[slot]}
          onChange={(event) => setDigit(slot, event.target.value)}
          onKeyDown={(event) => onKeyDown(slot, event)}
          onPaste={onPaste}
          onFocus={(event) => event.currentTarget.select()}
          className={cn(
            "h-14 w-12 rounded-[var(--radius-md)] border bg-[var(--color-surface)] text-center text-[1.5rem] font-bold tabular-nums tracking-[0] text-[var(--color-ink)] sm:h-16 sm:w-14 sm:text-[1.75rem]",
            "transition-[border-color,box-shadow,transform] duration-[var(--dream-duration-fast)] ease-[var(--dream-ease-standard)]",
            "focus:outline-none focus:border-brand-500 focus:shadow-[var(--dream-focus-ring)]",
            invalid
              ? "border-[#F44A4A] focus:border-[#F44A4A]"
              : "border-[var(--color-line-strong)] hover:border-[var(--color-ink-subtle)]",
            "disabled:cursor-not-allowed disabled:bg-[var(--color-surface-muted)]",
          )}
        />
      ))}
    </fieldset>
  );
}
